import type { NdtCertificateStatus } from "./ndt-asset-history.js";

/**
 * Calibration certificate status (Bloco 16) — always derived, never typed by
 * the user: a validity in the future is VÁLIDA, one in the past is VENCIDA,
 * and no validity at all is NÃO INFORMADA.
 */
export function resolveCalibrationStatus(
  expirationDate?: string | null,
  reference: Date = new Date(),
  thresholdDays?: number
): NdtCertificateStatus {
  if (!expirationDate || !expirationDate.trim()) return "NO_EXPIRATION_DEFINED";
  const expiry = new Date(expirationDate);
  if (isNaN(expiry.getTime())) return "NO_EXPIRATION_DEFINED";
  if (expiry.getTime() < reference.getTime()) return "EXPIRED";
  if (typeof thresholdDays === "number" && thresholdDays > 0) {
    const diffDays = (expiry.getTime() - reference.getTime()) / (1000 * 60 * 60 * 24);
    if (diffDays <= thresholdDays) {
      return "EXPIRING";
    }
  }
  return "VALID";
}

export function isCalibrationExpired(expirationDate?: string | null, reference: Date = new Date()): boolean {
  return resolveCalibrationStatus(expirationDate, reference) === "EXPIRED";
}

export const CALIBRATION_STATUS_LABELS: Record<NdtCertificateStatus, string> = {
  VALID: "VÁLIDA",
  EXPIRING: "PRÓXIMA DO VENCIMENTO",
  EXPIRED: "VENCIDA",
  NO_EXPIRATION_DEFINED: "NÃO INFORMADA"
};

export function calibrationStatusLabel(expirationDate?: string | null, reference: Date = new Date()): string {
  return CALIBRATION_STATUS_LABELS[resolveCalibrationStatus(expirationDate, reference)];
}

/**
 * "FAIXA" of a calibration block, derived from the step thicknesses actually
 * registered ("2,5 a 25,0 mm"). Falls back to the overall thickness and
 * returns empty when neither was registered — nothing is invented.
 */
export function formatCalibrationBlockRange(block: any): string {
  const steps = Array.isArray(block?.steps) ? block.steps : [];
  const thicknesses = steps
    .map((s: any) => Number(s?.nominalThickness))
    .filter((n: number) => Number.isFinite(n));

  if (thicknesses.length > 0) {
    const min = Math.min(...thicknesses);
    const max = Math.max(...thicknesses);
    const unit = steps[0]?.unit || "mm";
    const fmt = (n: number) => String(n).replace(".", ",");
    return min === max ? `${fmt(min)} ${unit}` : `${fmt(min)} a ${fmt(max)} ${unit}`;
  }

  const overall = Number(block?.technicalData?.overallThickness);
  return Number.isFinite(overall) && overall > 0 ? `${String(overall).replace(".", ",")} mm` : "";
}
